import { type AskPunttChatUserSummary } from "@mg/schemas/src/christo/askPuntt";

import { compareEnterpriseUsers } from "../../../utils/sortEnterpriseUsers";

export type InviteTier = "project" | "enterprise" | "new_email";

export type InvitePickerMode = "project" | "chat";

export interface InviteCandidateContext {
  mode: InvitePickerMode;
  /** Users who already have access to the target resource. */
  accessUserIds: Set<string>;
  /** Project roster (owner + allowList) for tier-1 tagging. */
  projectMemberIds: Set<string>;
}

export interface ClassifiedInviteCandidate {
  user: AskPunttChatUserSummary;
  tier: InviteTier;
  tagLabel: string;
  canInvite: boolean;
  disabledReason?: string;
}

export interface NewEmailInviteCandidate {
  tier: "new_email";
  email: string;
  tagLabel: string;
  canInvite: true;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isInviteEmailShape(value: string): boolean {
  return EMAIL_RE.test(value.trim());
}

export function classifyInviteCandidate(
  user: AskPunttChatUserSummary,
  context: InviteCandidateContext,
): ClassifiedInviteCandidate {
  const hasAccess = context.accessUserIds.has(user._id);
  const inProjectRoster = context.projectMemberIds.has(user._id);

  if (inProjectRoster) {
    return {
      user,
      tier: "project",
      tagLabel: "In project",
      canInvite: !hasAccess,
      disabledReason: hasAccess ? "Already has access" : undefined,
    };
  }

  return {
    user,
    tier: "enterprise",
    tagLabel: "In your organization",
    canInvite: !hasAccess,
    disabledReason: hasAccess ? "Already has access" : undefined,
  };
}

export function buildClassifiedSearchResults(
  users: readonly AskPunttChatUserSummary[],
  context: InviteCandidateContext,
): ClassifiedInviteCandidate[] {
  return users
    .map((user) => classifyInviteCandidate(user, context))
    .sort((a, b) => {
      const tierOrder = (tier: InviteTier) =>
        tier === "project" ? 0 : tier === "enterprise" ? 1 : 2;
      const tierDiff = tierOrder(a.tier) - tierOrder(b.tier);
      if (tierDiff !== 0) return tierDiff;
      return compareEnterpriseUsers(a.user, b.user);
    });
}

export function buildNewEmailCandidate(
  email: string,
): NewEmailInviteCandidate | null {
  const trimmed = email.trim();
  if (!isInviteEmailShape(trimmed)) return null;
  return {
    tier: "new_email",
    email: trimmed,
    tagLabel: "New email",
    canInvite: true,
  };
}

export function shouldOfferNewEmailInvite(
  query: string,
  directoryResults: readonly AskPunttChatUserSummary[],
): boolean {
  const candidate = buildNewEmailCandidate(query);
  if (!candidate) return false;
  const normalized = candidate.email.toLowerCase();
  return !directoryResults.some(
    (u) => u.email?.trim().toLowerCase() === normalized,
  );
}
